/** 校宝 Excel 导入按钮:选文件 → 预览(dry run)→ 确认导入。博颂 2026-09-22。 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { uploadXlsx, type ImportResult } from "../../api/gpa";

export function ImportButton({ label, urlFor, summarize, onDone, className }: {
  label: string;
  urlFor: (dryRun: boolean) => string;
  summarize: (r: ImportResult) => string[];
  onDone?: () => void;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string[] | null>(null);

  async function pick(f: File) {
    setFile(f); setErr(null); setDone(null); setBusy(true);
    try { setPreview(await uploadXlsx(urlFor(true), f)); } catch (e) { setErr((e as Error).message); setPreview(null); } finally { setBusy(false); }
  }
  async function apply() {
    if (!file) return;
    setBusy(true); setErr(null);
    try { const r = await uploadXlsx(urlFor(false), file); setDone(summarize(r)); setPreview(null); setFile(null); onDone?.(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <span className="relative inline-block">
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ""; }} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} className={className ?? "rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"}>
        {busy ? t("gpa.import.busy") : label}
      </button>
      {(preview || err || done) && (
        <div className="absolute right-0 z-20 mt-1 w-80 rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-lg">
          {err && <p className="text-rose-600">{err}</p>}
          {done && <><p className="mb-1 font-medium text-emerald-700">{t("gpa.import.done")}</p><ul className="list-disc pl-4 text-slate-600">{done.map((l, i) => <li key={i}>{l}</li>)}</ul><button type="button" className="mt-2 text-sky-600" onClick={() => setDone(null)}>{t("common.close")}</button></>}
          {preview && (
            <>
              <p className="mb-1 font-medium text-slate-800">{t("gpa.import.preview")}</p>
              <ul className="list-disc pl-4 text-slate-600">{summarize(preview).map((l, i) => <li key={i}>{l}</li>)}</ul>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => void apply()} className="rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600">{t("gpa.import.apply")}</button>
                <button type="button" onClick={() => { setPreview(null); setFile(null); }} className="rounded border border-slate-300 px-3 py-1">{t("common.cancel")}</button>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
